//@ts-strict-ignore
import React, { ReactElement, useContext, useMemo, useState } from 'react';
import { InfoButton } from '../../components/connectedForm/Button';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import { openFormActionInNewTab } from '../../utils/CheckPopUpBlockerFunction';
import { IFormAction, IFormActions } from '../../@types/entityTypes';
import { TaskPermission } from '../../graphql/entities/diary/DiaryEvent';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { ICustomer } from '../../graphql/schema/p4types';
import CustomerStatementModal from '../../components/modals/CustomerStatementModal';
import { useDispatch } from 'react-redux';
import WorkspaceContext, { WorkspaceContextType } from '../../components/Context/WorkspaceContext';
import { useHistory } from 'react-router';
import SalesOrder from '../../graphql/entities/sales/SalesOrder';
import Quotation from '../../graphql/entities/sales/Quotation';

export enum CustomerAction {
  PrintStatement = 'SALES_DOCUMENTS_PRINT_STATEMENTS',
  SalesOrderCreate = 'SALES_ORDERS_CREATE',
  SalesQuotesCreate = 'SALES_QUOTES_CREATE',
}

export const formActionsForPermissions: IFormActions = [
  {
    description: 'Add quote',
    actionReference: CustomerAction.SalesQuotesCreate,
  },
  {
    description: 'Add order',
    actionReference: CustomerAction.SalesOrderCreate,
  },
  {
    description: 'Add template',
    actionReference: CustomerAction.SalesOrderCreate,
  },
  {
    description: 'Add task',
    actionReference: TaskPermission.Create,
  },
  {
    actionReference: CustomerAction.PrintStatement,
  },
];

function useOpenFormActionInThisTab<TargetLocationState = any>() {
  const history = useHistory();

  return (url: string, locationState: Partial<TargetLocationState>) => {
    history.push({
      pathname: url,
      state: {
        previousLocation: history.location?.pathname || 'back', //Override returnUrl to go back to the Customer Form.
        randomKey: Math.random().toString(), //Provide a random number with the push for determining when a second or further order is added.
        modal: true,
        ...locationState,
      },
    });
  };
}

const usePrintStatementAction: () => [IFormAction<CustomerAction>, ReactElement] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedFormContext = useContext(ConnectedFormContext);

  const printStatementAction: IFormAction<CustomerAction> = {
    key: 'printStatement',
    text: 'Print Statement',
    condition: (customer: ICustomer) =>
      !customer?.archived && !!customer?.customerStatementDocumentDefault?.documentRequired,
    actionReference: CustomerAction.PrintStatement,
    onClick: () => {
      setModalOpen(true);
    },
  };

  // As we don't have a workspace context in this instance, we'll create a mock context that returns the single
  // customer from this form
  const mockWorkspaceContextValues: WorkspaceContextType = useMemo(() => {
    return {
      selectionState: {
        count: 1,
        actionEntities: [{ id: connectedFormContext.values?.id as number }],
        filters: [],
        selectedAll: false,
      },
    } as unknown as WorkspaceContextType;
  }, [connectedFormContext.values?.id]);

  return [
    printStatementAction,
    <WorkspaceContext.Provider value={mockWorkspaceContextValues}>
      <CustomerStatementModal modalOpen={modalOpen} onClose={() => setModalOpen(false)} singleCustomer={true} />
    </WorkspaceContext.Provider>,
  ];
};

export const useCustomerFormActions: (
  pageView?: boolean
) => [Array<IFormAction<CustomerAction | TaskPermission>>, ReactElement] = (pageView) => {
  const [printStatementAction, printStatementModals] = usePrintStatementAction();
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const openFormActionInThisTab = useOpenFormActionInThisTab();

  function addSalesOrder() {
    if (hasValidationErrors(connectedForm)) return;
    openFormActionInThisTab(`${SalesOrder.formUrl}New`, {
      defaultValues: [{ field: 'customerId', value: connectedForm.values?.id }],
    });
  }

  function addQuote() {
    if (hasValidationErrors(connectedForm)) return;
    openFormActionInThisTab(`${Quotation.formUrl}New`, {
      defaultValues: [{ field: 'customerId', value: connectedForm.values?.id }],
    });
  }

  function templateOrder() {
    if (hasValidationErrors(connectedForm)) return;
    openFormActionInThisTab(`/Sales/Order/Template/New`, {
      defaultValues: [
        { field: 'customerId', value: connectedForm.values?.id },
        { field: 'templated', value: 'true' },
      ],
    });
  }

  function addEvent() {
    if (hasValidationErrors(connectedForm)) return;
    openFormActionInNewTab(dispatch, `/Diary/Task/Details/New/Customer/${connectedForm.values?.id}`);
  }

  const olderActions: Array<IFormAction<CustomerAction | TaskPermission>> = [
    {
      text: 'Add quote',
      onClick: addQuote,
      individualButton: true,
      actionReference: CustomerAction.SalesQuotesCreate,
      condition: (values) => {
        return !(values.archived || pageView);
      },
      buttonProps: {
        'data-testid': 'customer-form-add-quote',
      },
    },
    {
      text: 'Add order',
      onClick: addSalesOrder,
      individualButton: true,
      actionReference: CustomerAction.SalesOrderCreate,
      condition: (values) => {
        return !(values.archived || pageView);
      },
      buttonProps: {
        'data-testid': 'customer-form-add-order',
      },
    },
    {
      text: 'Add template',
      onClick: templateOrder,
      individualButton: true,
      actionReference: CustomerAction.SalesOrderCreate,
      buttonProps: {
        'data-testid': 'customer-form-add-template',
      },
      condition: () => {
        return !pageView;
      },
    },
    {
      text: 'Add task',
      onClick: addEvent,
      individualButton: true,
      actionReference: TaskPermission.Create,
      actionIgnore: true, //Exclude from 'customers' submenu. Display only against event.
      buttonProps: {
        'data-testid': 'customer-form-add-task',
      },
    },
  ];
  const customerActions: Array<IFormAction<CustomerAction | TaskPermission>> = [...olderActions, printStatementAction];
  return [customerActions, <>{printStatementModals}</>];
};
